import { Button, InputField, Link } from '@branchjs/neo-components';
import React, { useState, type ChangeEvent } from 'react';
import { useHistory } from 'react-router-dom';
import LoggedoutActions from '../../../main/actions/LoggedoutActions';
import LoggedOutStore from '../../../main/stores/LoggedoutStore';
import { emailValid } from '../ScreenSignupValidation';
import LoggedOutContainer from '../components/LoggedOutContainer/LoggedOutContainer';
import { ERROR_MESSAGES } from '../constants/errorMessages.constants';
import { useStyles } from './ScreenResetPassword.styles';

export const ScreenResetPassword = ({ userEmailAddress }: { userEmailAddress: string | null }) => {
  const { formInputAndButtonAndFooterContainer, formInput, button, footer } = useStyles();

  const history = useHistory();
  const [emailAddressInput, setEmailAddressInput] = useState(userEmailAddress || '');
  const [errorMessage, setErrorMessage] = useState('');

  const onFormInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const email = event.target.value;
    LoggedoutActions.clearErrors();
    setErrorMessage('');
    setEmailAddressInput(email);
  };

  const handleServerErrors = (errors: Record<string, any>): void => {
    if (errors.status === 429) {
      setErrorMessage(ERROR_MESSAGES.RATE_LIMIT);
    } else {
      setErrorMessage(ERROR_MESSAGES.UNKNOWN);
    }
  };

  const sendPasswordResetEmail = async (): Promise<boolean> => {
    await LoggedoutActions.sendResetEmail(emailAddressInput);
    const errors = LoggedOutStore.getErrors();
    if (errors.data) {
      handleServerErrors(errors);
      return false;
    }
    return true;
  };

  const onButtonClick = async (): Promise<void> => {
    const emailIsValid = emailValid(emailAddressInput);
    if (!emailIsValid) {
      setErrorMessage(ERROR_MESSAGES.INVALID_EMAIL);
      return;
    }
    const sendPasswordResetEmailWasSuccessful = await sendPasswordResetEmail();
    if (sendPasswordResetEmailWasSuccessful) {
      localStorage.setItem('reset_password_user_email', emailAddressInput);
      history.push('/reset-password-link-sent', { emailSentRouteEnabled: true });
    }
  };

  return (
    <LoggedOutContainer title="Reset password" linkTitle="New user?" linkText="Create an account" linkPathName="/">
      <div className={formInputAndButtonAndFooterContainer}>
        <InputField
          className={formInput}
          id="reset-password__email-address"
          fullWidth
          title="Email address"
          placeholder="Enter your email address"
          value={emailAddressInput}
          onChange={onFormInputChange}
          error={!!errorMessage.length}
          helperText={errorMessage.length ? errorMessage : undefined}
        />
        <Button id="reset-password__continue-button" className={button} variant="contained" onClick={onButtonClick}>
          Continue
        </Button>
        <div className={footer}>
          Questions? We are here to{' '}
          <Link id="reset-password__help-link" href="https://support.branch.io/" target="_blank">
            help.
          </Link>
        </div>
      </div>
    </LoggedOutContainer>
  );
};
